import React, { Component, ReactNode } from 'react';
import { errorLogger } from '../../services/errorLogger';
import { statePreserver } from '../../services/statePreserver';

interface StreamErrorBoundaryProps {
  children: ReactNode;
  fallbackMessage?: string;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface StreamErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  retryCount: number;
  showFallback: boolean;
}

export class StreamErrorBoundary extends Component<StreamErrorBoundaryProps, StreamErrorBoundaryState> {
  private readonly MAX_RETRIES = 3;
  private readonly COMPONENT_NAME = 'StreamErrorBoundary';

  constructor(props: StreamErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
      showFallback: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<StreamErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error
    const canContinue = errorLogger.logError(error, this.COMPONENT_NAME, {
      componentStack: errorInfo.componentStack,
      retryCount: this.state.retryCount
    });

    // Preserve any streaming state if possible
    this.preserveStreamingState();

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // If component is unhealthy, show fallback immediately
    if (!canContinue || !errorLogger.isComponentHealthy(this.COMPONENT_NAME)) {
      this.setState({ showFallback: true });
    }
  }

  private preserveStreamingState = (): void => {
    try {
      // Try to preserve any streaming content that was received
      const streamingElements = document.querySelectorAll('[data-streaming="true"]');
      if (streamingElements.length > 0) {
        const content = Array.from(streamingElements).map(el => el.textContent || '').join('\n');
        if (content.trim()) {
          statePreserver.checkpoint('streaming_content', {
            content,
            timestamp: new Date().toISOString(),
            recoveredFromError: true
          });
        }
      }
    } catch (error) {
      console.warn('Failed to preserve streaming state:', error);
    }
  };

  private handleRetry = (): void => {
    if (this.state.retryCount < this.MAX_RETRIES) {
      this.setState(prevState => ({
        hasError: false,
        error: undefined,
        retryCount: prevState.retryCount + 1,
        showFallback: false
      }));
    } else {
      this.setState({ showFallback: true });
    }
  };

  private handleFallbackMode = (): void => {
    this.setState({ showFallback: true });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.state.showFallback) {
        return (
          <div className="border border-border rounded-lg p-4 bg-card">
            <div className="text-destructive text-sm font-medium mb-2">
              ⚠️ Streaming temporarily unavailable
            </div>
            <div className="text-muted-foreground text-sm mb-4">
              {this.props.fallbackMessage || "The chat is experiencing issues with real-time responses. You can still send messages, but they'll be processed without live streaming."}
            </div>
            <div className="flex gap-2">
              {this.state.retryCount < this.MAX_RETRIES && (
                <button
                  onClick={this.handleRetry}
                  className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                  Retry Streaming ({this.MAX_RETRIES - this.state.retryCount} left)
                </button>
              )}
              <button
                onClick={this.handleFallbackMode}
                className="px-3 py-1 text-sm bg-muted text-muted-foreground rounded hover:bg-muted/80"
              >
                Continue without streaming
              </button>
            </div>
            {/* Show any preserved content */}
            {this.renderPreservedContent()}
          </div>
        );
      } else {
        return (
          <div className="border border-border rounded-lg p-4 bg-card">
            <div className="text-destructive text-sm font-medium mb-2">
              ⚠️ Streaming interrupted
            </div>
            <div className="text-muted-foreground text-sm mb-4">
              The stream was interrupted. You can retry or switch to standard mode.
            </div>
            <div className="flex gap-2">
              <button
                onClick={this.handleRetry}
                className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                Retry
              </button>
              <button
                onClick={this.handleFallbackMode}
                className="px-3 py-1 text-sm bg-muted text-muted-foreground rounded hover:bg-muted/80"
              >
                Use fallback
              </button>
            </div>
            {this.renderPreservedContent()}
          </div>
        );
      }
    }

    return this.props.children;
  }

  private renderPreservedContent = (): ReactNode => {
    const preserved = statePreserver.recover('streaming_content');
    if (preserved && preserved.content) {
      return (
        <div className="mt-4 p-3 bg-muted rounded border-l-4 border-primary">
          <div className="text-xs text-muted-foreground mb-2">
            Recovered partial response:
          </div>
          <div className="text-sm">{preserved.content as string}</div>
        </div>
      );
    }
    return null;
  };
}